import ChainedBackend from "i18next-chained-backend"
import i18next from "i18next"
import LocalStorageBackend from "i18next-localstorage-backend"
import localStorageCheck from "lib/localStorageCheck"
import ping from "lib/ping"
import XHRBackend from "i18next-xhr-backend"

// An abstraction and wrapper around i18next to allow
// us to provide access to translations via file local imports.
//
// If a locale is set on window, it will use that to key into
// the translations. Otherwise it will fall back to en_AU.
//
// If translations have not been loaded, it will fall back to
// key passthrough.
//
// Usage:
//
//   import i18n from "lib/i18n";
//
//   i18n.t("my.translation");
//
export default class I18n {
  static init(loadPath, locale, crossDomain, callback) {
    const backends = []

    if (localStorageCheck()) {
      backends.push(LocalStorageBackend)
    }

    backends.push(XHRBackend) // default

    const start = (phraseIce) => {
      i18next.use(ChainedBackend).init(
        {
          backend: {
            backends,
            backendOptions: [
              // array of options in order of backends above
              {
                versions: {
                  [locale]: loadPath,
                },
              },
              {
                crossDomain,
                loadPath,
              },
            ],
          },
          lng: locale,
          postProcess: phraseIce ? ["phraseInContextEditor"] : [],
        },
        (err) => {
          if (err && err.length) {
            ping(`/no_js_locale?message=${err[0]}`)
          }

          try {
            callback()
          } catch (e) {
            ping(`/js_init_failure?message=${e}`)
            throw e
          }
        },
      )
    }

    // `PHRASEAPP_CONFIG` is only on the page when the in-context editor was
    // asked for, which is never in production. Importing the post processor
    // here rather than at the top keeps it in an async chunk of its own, so no
    // entry point carries it, and leaves this method synchronous for everyone
    // else: the promise below is only ever created when the editor is on.
    if (!window.PHRASEAPP_CONFIG) {
      start(false)
      return
    }

    import("i18next-phrase-in-context-editor-post-processor")
      .then(({ default: PhrasePostProcessor }) => {
        i18next.use(new PhrasePostProcessor({ phraseEnabled: true, scriptAutoLoad: false }))
        start(true)
      })
      .catch((e) => {
        // A chunk that fails to load must not take the page with it. Without
        // this, `callback` never runs, so `tc:initialized` never fires and
        // nothing waiting on it ever mounts. Carry on without markers.
        ping(`/js_init_failure?message=${e}`)
        start(false)
      })
  }

  static t(...args) {
    return i18next.t(...args) || args[0]
  }
}
